import * as defs from './ai-error-defs.js';

/** Normalized error buckets per general error type */
export const BUCKET = {
    UNAUTHORIZED: 'unauthorized',
    NOT_SUBSCRIBED: 'not_subscribed',
    AUDIO_ONLY: 'audio_only',
    NO_TOKENS: 'no_tokens',
    CONVERSATION_GONE: 'conversation_gone',
    MAINTENANCE: 'maintenance',
    STREAM_FAILED: 'stream_failed',
    SERVER_ERROR: 'server_error',
    OFFLINE: 'offline',
};

const BUCKET_VALUES = new Set(Object.values(BUCKET));

export const BUCKET_MESSAGE = {
    [BUCKET.UNAUTHORIZED]: defs.UNAUTHORIZED_MSG,
    [BUCKET.NOT_SUBSCRIBED]: defs.NO_SUBSCRIPTION_MSG,
    [BUCKET.AUDIO_ONLY]: defs.AUDIO_ONLY_MSG,
    [BUCKET.NO_TOKENS]: defs.getNoTokensMessage,
    [BUCKET.CONVERSATION_GONE]: defs.CONVERSATION_EXPIRED_MSG,
    [BUCKET.MAINTENANCE]: defs.MAINTENANCE_ERROR,
    [BUCKET.STREAM_FAILED]: defs.STREAM_ERROR,
    [BUCKET.SERVER_ERROR]: defs.GENERIC_ERROR_MSG,
    [BUCKET.OFFLINE]: defs.OFFLINE_MSG,
};

// uvicorn caps status codes at 599; 700 is a legacy maintenance code. Treat both identically.
const MAINTENANCE_STATUSES = new Set([599, 700]);

// Backend code -> bucket
const CODE_BUCKET = {
    unauthorized: BUCKET.UNAUTHORIZED,
    no_lang_tokens: BUCKET.NO_TOKENS,
    audio_only: BUCKET.AUDIO_ONLY,
    no_subscription: BUCKET.NOT_SUBSCRIBED,
    no_ai_access: BUCKET.NOT_SUBSCRIBED,
    anonymous: BUCKET.NOT_SUBSCRIBED,
    no_conversation: BUCKET.CONVERSATION_GONE,
    maintenance: BUCKET.MAINTENANCE,
    maintenance_legacy: BUCKET.MAINTENANCE,
};

function classify(status, code = null) {
    status = Number(status) || null;

    if (status && MAINTENANCE_STATUSES.has(status)) return BUCKET.MAINTENANCE;
    if (code && CODE_BUCKET[code]) return CODE_BUCKET[code];
    if (status === 401) return BUCKET.UNAUTHORIZED;
    if (status === 402) return BUCKET.NO_TOKENS;
    if (status === 404 || status === 409) return BUCKET.CONVERSATION_GONE;

    return BUCKET.SERVER_ERROR;
}

function make(bucket, { status = null, code = null, message = null } = {}) {
    return { bucket, status, code, rawMessage: message };
}

function isAiError(value) {
    return typeof value === 'object' && BUCKET_VALUES.has(value.bucket);
}

async function safeJson(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

// Pull { status, code, message } triplet from:
// - ApiError { apiError: { status, code, message } }
// - NetworkError { status, message }
function extractApiError(body) {
    if (!body || typeof body !== 'object') return {};

    if (body.apiError && typeof body.apiError === 'object') {
        const e = body.apiError;
        return { status: e.status, code: e.code, message: e.message };
    }

    if (body.status !== undefined || body.message !== undefined) {
        return { status: body.status, code: body.code, message: body.message };
    }

    return {};
}

// Not-quite-JSON SSE "Error"
function fromString(text) {
    try {
        const e = extractApiError(JSON.parse(text));
        if (e.status || e.code) return make(classify(e.status, e.code), e);
    } catch {
        // not JSON
    }

    const matched = text.match(/\((\d{3})\)/); // extract (status) if exists
    const status = matched ? Number(matched[1]) : null;
    return make(status ? classify(status) : BUCKET.STREAM_FAILED, { status, message: text });
}

/** Normalize anything a failed external request can hand us into { bucket, status, code, rawMessage } */
export async function toAiError(source) {
    // Already normalized
    if (isAiError(source)) return source;

    // Bucket string
    if (typeof source === 'string' && BUCKET_VALUES.has(source)) {
        return make(source);
    }

    // Network error (fetch() failed)
    if (source instanceof TypeError) {
        return make(BUCKET.OFFLINE, { message: source.message });
    }

    // fetch Response (handed in on failure)
    if (typeof Response !== 'undefined' && source instanceof Response) {
        const e = extractApiError(await safeJson(source.clone()));
        const status = e.status ?? source.status;
        return make(classify(status, e.code), { status, code: e.code, message: e.message });
    }

    // fetch-sse onError object ({ data: {...} })
    if (source && typeof source === 'object' && source.data && typeof source.data === 'object') {
        const d = source.data;
        return make(classify(d.status, d.code), { status: d.status, code: d.code, message: d.message });
    }

    // SSE "Error" event text payload
    if (typeof source === 'string') {
        return fromString(source);
    }

    // Parsed error body ({ apiError } or { status, message })
    if (typeof source === 'object' && (source.apiError || source.status !== undefined)) {
        const e = extractApiError(source);
        return make(classify(e.status, e.code), e);
    }

    // Fallback
    const message = source instanceof Error ? source.message : String(source);
    return make(BUCKET.SERVER_ERROR, { message });
}

/** fetch() wrapper that guarantees a normalized AiError on failure */
export async function aiFetch(url, options) {
    let response;

    try {
        response = await fetch(url, options);
    } catch (networkError) {
        throw await toAiError(networkError);
    }

    if (!response.ok) {
        throw await toAiError(response);
    }

    return response;
}
